// client/src/pages/EmployeeDashboardPage.js
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Container,
  Alert,
  Table,
  Form,
  Button,
  Card,
  Badge,
  Collapse,
  Row,
  Col,
  Modal
} from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { getAuthHeaders } from '../utils/authHelpers';
import TaskGroup from '../components/TaskGroup';
import '../styles/OrderList.css';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ORDERS_API_URL = `${API_ROOT_URL}/orders`;

// ─── Progress Box ────────────────────────────────────────────────────────────
function ProgressBox({ status }) {
  let backgroundColor = '#e9ecef';
  if (status === 'complete') backgroundColor = '#198754';
  else if (status === 'partial') backgroundColor = '#ffc107';

  return (
    <div
      style={{
        height: '24px',
        margin: '2px',
        borderRadius: '4px',
        backgroundColor,
        transition: 'background-color 0.3s ease',
      }}
    />
  );
}

// ─── Progress Logic Helpers ───────────────────────────────────────────────────
function getFrameStatus(order) {
  const m = order.manufacturing || {};
  const isNewCover = order.orderType === 'New Cover';
  const upperDone = !!m.upperFrameCompleted;
  const lowerDone = !!m.lowerFrameCompleted;

  if (isNewCover) {
    if (upperDone && lowerDone) return 'complete';
    if (upperDone || lowerDone) return 'partial';
    return 'none';
  }
  return upperDone || lowerDone ? 'complete' : 'none';
}

function getUpperFabricStatus(m) {
  if (m.upperFabricBoxedUp) return 'complete';
  if (
    m.upperAndLowerFabricsDrawingsCompleted ||
    m.upperAndLowerFabricsCut ||
    m.upperFabricToFit ||
    m.upperFabricInProgress ||
    m.upperFabricCompleted
  ) return 'partial';
  return 'none';
}

function getLowerFabricStatus(m) {
  if (m.lowerFabricAndCableLinersBoxedUp) return 'complete';
  if (
    m.upperAndLowerFabricsDrawingsCompleted ||
    m.upperAndLowerFabricsCut ||
    m.lowerFabricInProgress ||
    m.lowerFabricCompleted
  ) return 'partial';
  return 'none';
}

function getExtraFabricStatus(m) {
  const groups = [
    { partial: 'chafePanelsInProgress',   complete: 'chafePanelsCompleted'   },
    { partial: 'pileCoversInProgress',    complete: 'pileCoversCompleted'    },
    { partial: 'fabricSkirtsInProgress',  complete: 'fabricSkirtsCompleted'  },
    { partial: 'birdPanelsInProgress',    complete: 'birdPanelsCompleted'    },
    { partial: 'dropPanelsInProgress',    complete: 'dropPanelsCompleted'    },
  ];
  const active = groups.filter(g => m[g.partial] || m[g.complete]);
  if (active.length === 0) return 'none';
  return active.every(g => !!m[g.complete]) ? 'complete' : 'partial';
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EmployeeDashboardPage() {
  const {
    user,
    allOrders,
    loading,
    error,
    submissionMessage,
    setSubmissionMessage,
    orderTaskStates,
    setOrderTaskStates,
    getTasksForStage,
    stageMap,
    formatDate,
    groupTasksForDisplay,
    fetchOrdersForRole,
  } = useDashboardData();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('All');
  const [expandedRows, setExpandedRows] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [displayMode, setDisplayMode] = useState(false);

  // ── Display mode: measured row budget ────────────────────────────────────
  // Holds the pixel height available per table row in display mode.
  // Recomputed by a ResizeObserver whenever the container or fullscreen changes.
  const [rowBudget, setRowBudget] = useState(40);
  const displayContainerRef = useRef(null);

  // Request/exit browser fullscreen whenever display mode toggles.
  useEffect(() => {
    if (displayMode) {
      const el = document.documentElement;
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      if (req) req.call(el).catch(() => {});
    } else {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
      if (exit && document.fullscreenElement) exit.call(document).catch(() => {});
    }
  }, [displayMode]);

  // Sync displayMode if the user exits fullscreen via the browser (Esc key, etc.)
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) setDisplayMode(false);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const topScrollRef = useRef(null);
  const topInnerRef = useRef(null);
  const tableScrollRef = useRef(null);

  /* ─── Filter Helpers ─────────────────────────────────────────────────────── */
  const orderTypes = useMemo(() => {
    const types = new Set(allOrders.map(o => o.orderType).filter(Boolean));
    return ['All', ...Array.from(types).sort()];
  }, [allOrders]);

  const filteredOrders = useMemo(() => {
    return allOrders.filter(o => {
      const s = searchTerm.toLowerCase();
      const matchesSearch =
        o.orderId.toLowerCase().includes(s) ||
        o.customerName?.toLowerCase().includes(s) ||
        o.invoiceNumber?.toLowerCase().includes(s) ||
        o.dealerName?.toLowerCase().includes(s);
      const matchesType = selectedOrderType === 'All' || o.orderType === selectedOrderType;
      return matchesSearch && matchesType;
    });
  }, [allOrders, searchTerm, selectedOrderType]);

  // Split into two groups based on whether a build start date exists, both sorted oldest → newest
  const inProgressOrders = useMemo(() => {
    return filteredOrders
      .filter(o => !!o.manufacturing?.orderEnteredProductionDate)
      .sort((a, b) => {
        const dateA = new Date(a.manufacturing.orderEnteredProductionDate);
        const dateB = new Date(b.manufacturing.orderEnteredProductionDate);
        return dateA - dateB;
      });
  }, [filteredOrders]);

  // Display mode shows all in-progress orders except Parts
  const displayModeOrders = useMemo(() => {
    return inProgressOrders.filter(o => o.orderType !== 'Parts');
  }, [inProgressOrders]);

  // ── ResizeObserver: recompute row budget whenever the display container
  //    changes size (entering fullscreen shifts the available height).
  //    totalRows = data rows + 1 header row. We subtract a fixed amount for
  //    the top bar + legend, then divide the remainder evenly.
  useEffect(() => {
    if (!displayMode) return;

    const TOP_CHROME_PX = 70; // top bar (~36px) + legend (~20px) + gaps

    const compute = () => {
      const el = displayContainerRef.current;
      if (!el) return;
      const containerH = el.clientHeight - TOP_CHROME_PX;
      const totalRows = displayModeOrders.length + 1; // +1 for thead
      const budget = Math.max(18, Math.floor(containerH / totalRows));
      setRowBudget(budget);
    };

    compute();

    const ro = new ResizeObserver(compute);
    if (displayContainerRef.current) ro.observe(displayContainerRef.current);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode, displayModeOrders.length]);

  const notScheduledOrders = useMemo(() => {
    return filteredOrders
      .filter(o => !o.manufacturing?.orderEnteredProductionDate)
      .sort((a, b) => {
        const dateA = a.orderDate ? new Date(a.orderDate) : new Date(0);
        const dateB = b.orderDate ? new Date(b.orderDate) : new Date(0);
        return dateA - dateB;
      });
  }, [filteredOrders]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedOrderType('All');
    setExpandedRows({});
  };

  /* ─── Task Helpers ───────────────────────────────────────────────────────── */
  const getTasksForOrder = useCallback(
    order => getTasksForStage(order.currentStage) || [],
    [getTasksForStage]
  );

  const initialTaskStates = useMemo(() => {
    const map = {};
    allOrders.forEach(order => {
      const stageKey = stageMap[order.currentStage];
      const stageData = order[stageKey] || {};
      const snapshot = {};
      getTasksForOrder(order).forEach(task => {
        if (task.type === 'checkbox') {
          snapshot[task.id] = stageData[task.id] || false;
        } else if (task.type === 'date') {
          snapshot[task.id] = stageData[task.id]
            ? new Date(stageData[task.id]).toISOString().split('T')[0]
            : '';
        }
      });
      map[order.orderId] = snapshot;
    });
    return map;
  }, [allOrders, stageMap, getTasksForOrder]);

  useEffect(() => {
    if (Object.keys(initialTaskStates).length === 0) return;

    setOrderTaskStates(prev => {
      const next = { ...prev };

      Object.entries(initialTaskStates).forEach(([orderId, freshSnapshot]) => {
        const current = prev[orderId];

        if (!current) {
          next[orderId] = freshSnapshot;
          return;
        }

        const merged = { ...current };
        Object.entries(freshSnapshot).forEach(([field, serverValue]) => {
          if (current[field] === prev[orderId]?.[field]) {
            merged[field] = serverValue;
          }
        });
        next[orderId] = merged;
      });

      return next;
    });
  }, [initialTaskStates]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!submissionMessage.message) return;
    const timer = setTimeout(() => setSubmissionMessage({ message: '', type: '' }), 3000);
    return () => clearTimeout(timer);
  }, [submissionMessage.message, setSubmissionMessage]);

  // Exit display mode on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && displayMode) setDisplayMode(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [displayMode]);

  // ─── Display Mode polling ────────────────────────────────────────────────
  useEffect(() => {
    if (!displayMode) return;
    const interval = setInterval(() => {
      fetchOrdersForRole();
    }, 10000);
    return () => clearInterval(interval);
  }, [displayMode, fetchOrdersForRole]);

  /* ─── Scroll Sync ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const topScroll = topScrollRef.current;
    const tableScroll = tableScrollRef.current;
    const topInner = topInnerRef.current;
    if (!topScroll || !tableScroll || !topInner) return;

    const syncWidths = () => {
      const tableElement = tableScroll.querySelector('table');
      if (tableElement) topInner.style.width = `${tableElement.scrollWidth}px`;
    };
    const handleTopScroll = () => { tableScroll.scrollLeft = topScroll.scrollLeft; };
    const handleTableScroll = () => { topScroll.scrollLeft = tableScroll.scrollLeft; };

    topScroll.addEventListener('scroll', handleTopScroll);
    tableScroll.addEventListener('scroll', handleTableScroll);
    syncWidths();
    const ro = new ResizeObserver(syncWidths);
    ro.observe(tableScroll);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      ro.disconnect();
    };
  }, [filteredOrders]);

  /* ─── Task Change / Dirty ────────────────────────────────────────────────── */
  const handleTaskChange = useCallback((orderId, taskId, value) => {
    setOrderTaskStates(prev => ({
      ...prev,
      [orderId]: { ...prev[orderId], [taskId]: value },
    }));
  }, [setOrderTaskStates]);

  const isOrderDirty = useCallback(order => {
    const current = orderTaskStates[order.orderId] || {};
    const initial = initialTaskStates[order.orderId] || {};
    return Object.keys(current).some(key => current[key] !== initial[key]);
  }, [orderTaskStates, initialTaskStates]);

  /* ─── TaskInputRow ───────────────────────────────────────────────────────── */
  const TaskInputRow = ({ order, task, currentOrderTaskState, handleTaskChange, loading, alignDesktop = 'start' }) => {
    const flexClass = alignDesktop === 'center'
      ? 'd-flex justify-content-center'
      : 'd-flex justify-content-center justify-content-md-start';
    const textClass = alignDesktop === 'center' ? 'text-center' : 'text-center text-md-start';

    return (
      <div className="mb-2 w-100">
        {task.type === 'checkbox' ? (
          <div className={flexClass}>
            <Form.Check type="checkbox" id={`task-${order.orderId}-${task.id}`} className="mb-0">
              <Form.Check.Input
                type="checkbox"
                checked={!!currentOrderTaskState[task.id]}
                onChange={e => handleTaskChange(order.orderId, task.id, e.target.checked)}
                disabled={loading || (task.editableBy && !task.editableBy.includes(user?.role))}
              />
              <Form.Check.Label className="ms-2">{task.label}</Form.Check.Label>
            </Form.Check>
          </div>
        ) : (
          <Form.Group controlId={`task-${order.orderId}-${task.id}`} className="mb-0 w-100">
            <Form.Label className={`mb-1 d-block ${textClass}`}>
              {task.label} {task.required && <span className="text-danger">*</span>}
            </Form.Label>
            <div className={flexClass}>
              <Form.Control
                type="date"
                value={currentOrderTaskState[task.id] ?? ''}
                onChange={e => handleTaskChange(order.orderId, task.id, e.target.value)}
                disabled={loading || (task.editableBy && !task.editableBy.includes(user?.role))}
                className="w-auto"
              />
            </div>
          </Form.Group>
        )}
      </div>
    );
  };

  /* ─── Save Handlers ──────────────────────────────────────────────────────── */
  const handleShowConfirm = () => {
    const dirtyCount = filteredOrders.filter(isOrderDirty).length;
    if (dirtyCount > 0) {
      setShowConfirmModal(true);
    } else {
      setSubmissionMessage({ message: 'No changes to save.', type: 'info' });
    }
  };

  const handleConfirmSave = async () => {
    setShowConfirmModal(false);
    const dirtyOrders = filteredOrders.filter(isOrderDirty);
    try {
      for (const order of dirtyOrders) {
        const stageKey = stageMap[order.currentStage];
        await fetch(`${ORDERS_API_URL}/${order.orderId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            [stageKey]: { ...(order[stageKey] || {}), ...orderTaskStates[order.orderId] },
            updatedAt: new Date(),
          }),
        });
      }
      await fetchOrdersForRole();
      setSubmissionMessage({ message: 'Tasks saved successfully.', type: 'success' });
    } catch (err) {
      setSubmissionMessage({ message: 'Failed to save tasks.', type: 'danger' });
    }
  };

  /* ─── Task Expansion Row ─────────────────────────────────────────────────── */
  // colSpan: 5 info cols + 9 progress boxes + 1 actions = 15
  const IN_PROGRESS_COLS = 15;

  const renderTaskExpansion = (order) => {
    const tasksForCurrentStage = getTasksForOrder(order);
    const currentOrderTaskState = orderTaskStates[order.orderId] || {};

    return (
      <tr key={`${order.orderId}-tasks`}>
        <td colSpan={IN_PROGRESS_COLS} className="p-0">
          <Collapse in={!!expandedRows[order.orderId]}>
            <div className="p-3 bg-light border-top">
              {tasksForCurrentStage.length === 0 ? (
                <span className="text-muted ms-2">No tasks defined for this stage.</span>
              ) : (
                <Row className="gx-4">
                  {(() => {
                    const grouped = groupTasksForDisplay(tasksForCurrentStage);
                    const seamstress = grouped.find(g => g.group === 'Seamstress Tasks');
                    const getS = name =>
                      seamstress?.subtasks?.find(s => s.subgroup === name) || { tasks: [] };

                    return (
                      <>
                        <Col md={4}>
                          {grouped
                            .filter(g =>
                              ['Manager Tasks', 'Welder Tasks', 'Assembler Tasks'].includes(g.group)
                            )
                            .map((group, idx) => (
                              <TaskGroup
                                key={idx}
                                group={group}
                                order={order}
                                currentOrderTaskState={currentOrderTaskState}
                                handleTaskChange={handleTaskChange}
                                loading={loading}
                                TaskInputRow={TaskInputRow}
                              />
                            ))}
                        </Col>
                        <Col md={4}>
                          <TaskGroup
                            group={{ group: 'Seamstress Tasks', subtasks: [getS('Drawing'), getS('Cutting')] }}
                            order={order}
                            currentOrderTaskState={currentOrderTaskState}
                            handleTaskChange={handleTaskChange}
                            loading={loading}
                            TaskInputRow={TaskInputRow}
                          />
                        </Col>
                        <Col md={4}>
                          <TaskGroup
                            group={{ group: 'Seamstress Tasks', subtasks: [getS('Sewing'), getS('Completing & Packing')] }}
                            order={order}
                            currentOrderTaskState={currentOrderTaskState}
                            handleTaskChange={handleTaskChange}
                            loading={loading}
                            TaskInputRow={TaskInputRow}
                          />
                        </Col>
                      </>
                    );
                  })()}
                </Row>
              )}
            </div>
          </Collapse>
        </td>
      </tr>
    );
  };

  /* ─── Guards ─────────────────────────────────────────────────────────────── */
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Employee') return <Navigate to="/dashboard" replace />;

  const hasDirtyOrders = filteredOrders.some(isOrderDirty);

  // ── Derived display-mode style values ────────────────────────────────────
  // Everything scales from rowBudget, which is recomputed by the ResizeObserver.
  // rowBudget is the exact pixel height each row (including header) gets.
  // Font size: 45% of row height, clamped 10–18px.
  // Cell padding: tight vertical, modest horizontal.
  // Progress box: 60% of row height so it fills the cell without overflow.
  const dmFontSize  = Math.min(18, Math.max(10, Math.floor(rowBudget * 0.45)));
  const dmPadV      = Math.max(0,  Math.floor(rowBudget * 0.08));
  const dmPadH      = Math.max(3,  Math.floor(rowBudget * 0.18));
  const dmBoxHeight = Math.max(6,  Math.floor(rowBudget * 0.60));

  /* ─── Render ─────────────────────────────────────────────────────────────── */
  return (
    <Container fluid className="bg-light min-vh-100 py-4">
      <Container>

        {/* ── Header Card ── */}
        <Card className="mb-4 shadow-sm text-center">
          <Card.Body>
            <h2>Welcome, {user.username}.</h2>
            <p className="text-muted mb-0">Manufacturing Task Dashboard</p>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}
        {submissionMessage.message && (
          <Alert variant={submissionMessage.type}>{submissionMessage.message}</Alert>
        )}

        {/* ── Filters + Save ── */}
        <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
          <div className="d-flex gap-2 flex-grow-1" style={{ maxWidth: '600px' }}>
            <Form.Control
              placeholder="Search by ID, Customer, Invoice, or Dealer..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Form.Select
              style={{ maxWidth: '200px' }}
              value={selectedOrderType}
              onChange={e => setSelectedOrderType(e.target.value)}
            >
              {orderTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
            <button type="button" onClick={handleClearFilters} className="btn btn-outline-secondary">
              Clear
            </button>
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="outline-dark"
              onClick={() => setDisplayMode(true)}
              title="Enter fullscreen display mode (TV view)"
            >
              <i className="bi bi-tv me-1"></i>
              Display Mode
            </Button>

            <Button
              variant={hasDirtyOrders ? 'success' : 'secondary'}
              disabled={loading || !hasDirtyOrders}
              onClick={handleShowConfirm}
            >
              {loading ? 'Saving...' : 'Save My Changes'}
            </Button>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            TABLE 1 — IN PROGRESS
        ════════════════════════════════════════════════════════════════════ */}
        <div className="mb-2 d-flex align-items-center gap-2">
          <h5 className="mb-0 text-primary">In Progress</h5>
          <Badge bg="primary">{inProgressOrders.length}</Badge>
        </div>

        <div className="mb-5">
          <div
            className="top-scroll"
            ref={topScrollRef}
            style={{ overflowX: 'auto', height: '15px', visibility: loading ? 'hidden' : 'visible' }}
          >
            <div className="top-scroll-inner" ref={topInnerRef} style={{ height: '1px' }} />
          </div>

          <div className="table-responsive shadow-sm rounded" ref={tableScrollRef}>
            <Table bordered hover className="order-list-table mb-0 bg-white align-middle" style={{ minWidth: '1300px' }}>
              <thead className="table-dark">
                <tr>
                  {/* Info columns */}
                  <th className="text-center" style={{ width: 120 }}>Invoice #</th>
                  <th className="text-center" style={{ width: 130 }}>Order Type</th>
                  <th className="text-center" style={{ width: 150 }}>Dealer Name</th>
                  <th className="text-center" style={{ width: 150 }}>Customer Name</th>
                  <th className="text-center" style={{ width: 120 }}>Build Start</th>
                  {/* Progress columns */}
                  <th className="text-center" style={{ width: 60 }}>Frame</th>
                  <th className="text-center" style={{ width: 70 }}>Brackets</th>
                  <th className="text-center" style={{ width: 60 }}>Motor</th>
                  <th className="text-center" style={{ width: 60 }}>Rods</th>
                  <th className="text-center" style={{ width: 60 }}>R/Sup</th>
                  <th className="text-center" style={{ width: 60 }}>Box</th>
                  <th className="text-center" style={{ width: 70 }}>Upper/F</th>
                  <th className="text-center" style={{ width: 70 }}>Lower/F</th>
                  <th className="text-center" style={{ width: 70 }}>Extra/F</th>
                  {/* Actions */}
                  <th className="text-center" style={{ width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={IN_PROGRESS_COLS} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 mb-0 text-muted">Loading production tasks...</p>
                    </td>
                  </tr>
                ) : inProgressOrders.length === 0 ? (
                  <tr>
                    <td colSpan={IN_PROGRESS_COLS} className="text-center py-4 text-muted">
                      No in-progress orders found.
                    </td>
                  </tr>
                ) : (
                  inProgressOrders.map(order => {
                    const m = order.manufacturing || {};
                    const dirty = isOrderDirty(order);
                    const isExpanded = !!expandedRows[order.orderId];

                    const buildStart = m.orderEnteredProductionDate;
                    const buildStartDisplay = buildStart
                      ? formatDate(buildStart, false)
                      : '—';

                    return (
                      <React.Fragment key={order.orderId}>
                        <tr className={dirty ? 'table-warning' : ''}>
                          {/* Info */}
                          <td>
                            {dirty && (
                              <span className="text-danger me-1 fw-bold" title="Unsaved changes">*</span>
                            )}
                            {order.invoiceNumber || 'N/A'}
                          </td>
                          <td>{order.orderType || 'N/A'}</td>
                          <td>{order.dealerName || 'N/A'}</td>
                          <td>{order.customerName || 'N/A'}</td>
                          <td><small>{buildStartDisplay}</small></td>

                          {/* Progress boxes */}
                          <td className="text-center p-1">
                            <ProgressBox status={getFrameStatus(order)} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={m.bracketsCompleted ? 'complete' : 'none'} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={m.motorCompleted ? 'complete' : 'none'} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={m.fiberglassRodsCompleted ? 'complete' : 'none'} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={m.ridgeSupportsCompleted ? 'complete' : 'none'} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={m.hardwareKitCompleted ? 'complete' : 'none'} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={getUpperFabricStatus(m)} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={getLowerFabricStatus(m)} />
                          </td>
                          <td className="text-center p-1">
                            <ProgressBox status={getExtraFabricStatus(m)} />
                          </td>

                          {/* Actions */}
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant={
                                order.currentStage === 'Completed'
                                  ? 'secondary'
                                  : 'outline-primary'
                              }
                              disabled={order.currentStage === 'Completed'}
                              onClick={() =>
                                setExpandedRows(p => ({
                                  ...p,
                                  [order.orderId]: !p[order.orderId],
                                }))
                              }
                            >
                              {isExpanded ? 'Hide Tasks' : 'View Tasks'}
                            </Button>
                          </td>
                        </tr>

                        {/* Expanded task panel */}
                        {renderTaskExpansion(order)}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            TABLE 2 — NOT SCHEDULED
        ════════════════════════════════════════════════════════════════════ */}
        <div className="mb-2 d-flex align-items-center gap-2">
          <h5 className="mb-0 text-secondary">Not Scheduled</h5>
          <Badge bg="secondary">{notScheduledOrders.length}</Badge>
        </div>

        <div className="table-responsive shadow-sm rounded mb-4">
          <Table bordered hover className="order-list-table mb-0 bg-white align-middle" style={{ minWidth: '800px' }}>
            <thead className="table-secondary">
              <tr>
                <th className="text-center" style={{ width: 130 }}>Invoice #</th>
                <th className="text-center" style={{ width: 140 }}>Order Type</th>
                <th className="text-center" style={{ width: 180 }}>Dealer Name</th>
                <th className="text-center" style={{ width: 180 }}>Customer Name</th>
                <th className="text-center">Custom Work</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <div className="spinner-border text-secondary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : notScheduledOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    No unscheduled orders found.
                  </td>
                </tr>
              ) : (
                notScheduledOrders.map(order => (
                  <tr key={order.orderId} className="text-muted">
                    <td>{order.invoiceNumber || 'N/A'}</td>
                    <td>{order.orderType || 'N/A'}</td>
                    <td>{order.dealerName || 'N/A'}</td>
                    <td>{order.customerName || 'N/A'}</td>
                    <td><small>{order.customWork || '—'}</small></td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            DISPLAY MODE — Fullscreen TV View
        ════════════════════════════════════════════════════════════════════ */}
        {displayMode && (
          <div
            ref={displayContainerRef}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              backgroundColor: '#111',
              display: 'flex',
              flexDirection: 'column',
              padding: '12px 16px',
              boxSizing: 'border-box',
              overflow: 'hidden', // never scroll — everything must fit
            }}
          >
            {/* Top bar */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '6px',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.05em' }}>
                  <i className="bi bi-bar-chart-steps me-2"></i>
                  PRODUCTION PROGRESS
                </span>
                <Badge bg="primary" style={{ fontSize: '0.8rem' }}>
                  {displayModeOrders.length} Orders In Progress
                </Badge>
              </div>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setDisplayMode(false)}
                title="Exit Display Mode (Esc)"
              >
                <i className="bi bi-fullscreen-exit me-1"></i>
                Exit
              </Button>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '6px', flexShrink: 0 }}>
              {[
                { color: '#198754', label: 'Complete' },
                { color: '#ffc107', label: 'In Progress' },
                { color: '#444',    label: 'Not Started' },
              ].map(({ color, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: 14, height: 14, borderRadius: 2, backgroundColor: color, border: '1px solid #555' }} />
                  <span style={{ color: '#ccc', fontSize: '0.72rem' }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Table — explicit row heights enforce the budget; no scrolling ever occurs */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  tableLayout: 'fixed',
                  fontSize: `${dmFontSize}px`,
                }}
              >
                <colgroup>
                  <col style={{ width: '8%'  }} /> {/* Invoice #   */}
                  <col style={{ width: '9%'  }} /> {/* Order Type  */}
                  <col style={{ width: '23%' }} /> {/* Dealer      */}
                  <col style={{ width: '22%' }} /> {/* Customer    */}
                  <col style={{ width: '8%'  }} /> {/* Build Start */}
                  <col style={{ width: '5%'  }} /> {/* Frame       */}
                  <col style={{ width: '5%'  }} /> {/* Brackets    */}
                  <col style={{ width: '5%'  }} /> {/* Box         */}
                  <col style={{ width: '5%'  }} /> {/* Upper/F     */}
                  <col style={{ width: '5%'  }} /> {/* Lower/F     */}
                  <col style={{ width: '5%'  }} /> {/* Extra/F     */}
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: '#1a1a2e', color: '#e0e0e0', height: `${rowBudget}px` }}>
                    {[
                      'Invoice #', 'Order Type', 'Dealer', 'Customer', 'Build Start',
                      'Frame', 'Brackets', 'Box', 'Upper/F', 'Lower/F', 'Extra/F',
                    ].map(col => (
                      <th
                        key={col}
                        style={{
                          padding: `${dmPadV}px ${dmPadH}px`,
                          textAlign: 'center',
                          fontWeight: 600,
                          borderBottom: '2px solid #333',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayModeOrders.length === 0 ? (
                    <tr>
                      <td colSpan={11} style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
                        No orders currently in progress.
                      </td>
                    </tr>
                  ) : (
                    displayModeOrders.map((order, idx) => {
                      const m = order.manufacturing || {};
                      const buildStart = m.orderEnteredProductionDate
                        ? formatDate(m.orderEnteredProductionDate, false)
                        : '—';
                      const rowBg = idx % 2 === 0 ? '#1c1c1c' : '#242424';

                      const progressCols = [
                        getFrameStatus(order),
                        m.bracketsCompleted        ? 'complete' : 'none',
                        m.hardwareKitCompleted     ? 'complete' : 'none',
                        getUpperFabricStatus(m),
                        getLowerFabricStatus(m),
                        getExtraFabricStatus(m),
                      ];

                      // All cells clip their content — no cell may ever grow the row
                      const textCell = {
                        padding: `${dmPadV}px ${dmPadH}px`,
                        color: '#ddd',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxHeight: `${rowBudget}px`,
                        textAlign: 'center',
                      };

                      return (
                        <tr key={order.orderId} style={{ backgroundColor: rowBg, height: `${rowBudget}px`, maxHeight: `${rowBudget}px` }}>
                          <td style={textCell}>{order.invoiceNumber || 'N/A'}</td>
                          <td style={textCell}>{order.orderType    || 'N/A'}</td>
                          <td style={textCell}>{order.dealerName   || 'N/A'}</td>
                          <td style={textCell}>{order.customerName || 'N/A'}</td>
                          <td style={{ ...textCell, color: '#aaa' }}>{buildStart}</td>
                          {progressCols.map((status, i) => {
                            let bg = '#333';
                            if (status === 'complete') bg = '#198754';
                            else if (status === 'partial') bg = '#ffc107';
                            return (
                              <td key={i} style={{ padding: `${dmPadV}px 3px`, textAlign: 'center', verticalAlign: 'middle' }}>
                                <div style={{
                                  height: `${dmBoxHeight}px`,
                                  borderRadius: '3px',
                                  backgroundColor: bg,
                                  transition: 'background-color 0.3s ease',
                                }} />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title>Confirm Save</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-4">
            <div className="text-center">
              <i className="bi bi-exclamation-circle text-warning" style={{ fontSize: '2rem' }}></i>
              <p className="mt-3 mb-0">
                You have modified tasks for{' '}
                <strong>{filteredOrders.filter(isOrderDirty).length}</strong> order(s).
              </p>
              <p className="text-muted small">Are you sure you want to update these records?</p>
            </div>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleConfirmSave}>
              Confirm & Save
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </Container>
  );
}